// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the app's words, in German or English.
//
// The page embeds every `mobile…` translation in BOTH languages (it cannot know
// whose device is asking — see mylibs/pqforce-mobile-language.php) and this
// module picks: the PQFORCE ACCOUNT's language once the profile says it, the
// phone's before that. DOM-free; `ui_mobile_language.ts` reads the page.
//
// ⚠ `t()` IS CALLED AT RENDER TIME, NEVER AT MODULE LEVEL. A module-level
// `const LABEL = t(…)` is evaluated once, at import, before any dictionary is
// loaded — it would freeze the English fallback for the life of the page, and
// switching language would not reach it. Keep a function, as the portal's own
// staff pages do (their `T` tables).
//
// ⚠ Every call is `t("mobile…", "English")` with LITERAL arguments — an id and a fallback string.
// MobileTranslationsTest scans for that shape to prove every id is seeded in
// both languages; an id built by concatenation, or passed through a variable,
// is invisible to it and ships unseeded.
// ─────────────────────────────────────────────────────────────────────────────

/** The two languages the app speaks, like the rest of the portal's own words. */
export type AppLanguage = "de" | "en";

type Dictionary = Record<string, string>;

let dictionaries: Record<AppLanguage, Dictionary> = { de: {}, en: {} };
let current: AppLanguage = "en";

/** A language code as the app's — anything that is not German is English. */
export function appLanguageOf(value: unknown): AppLanguage {
    return String(value ?? "")
        .trim()
        .toLowerCase()
        .startsWith("de")
        ? "de"
        : "en";
}

/** Load the page's embedded strings: `{ de: {id: text}, en: {id: text} }`. Junk loads as empty. */
export function loadStrings(raw: unknown): void {
    const pick = (value: unknown): Dictionary => {
        if (!value || typeof value !== "object") return {};
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).filter(([, text]) => typeof text === "string"),
        ) as Dictionary;
    };
    const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};

    dictionaries = { de: pick(source.de), en: pick(source.en) };
}

export function setLanguage(language: AppLanguage): void {
    current = language;
}

export function currentLanguage(): AppLanguage {
    return current;
}

/**
 * The language to START in: what this device used last, else what the page decided from the phone.
 *
 * The stored one wins because it is the ACCOUNT's, learned on an earlier start — without it a German
 * user on an English phone would see an English first frame every time until `me` answered.
 */
export function startLanguage(stored: unknown, pageLanguage: unknown): AppLanguage {
    return stored === "de" || stored === "en" ? stored : appLanguageOf(pageLanguage);
}

/**
 * What the person chose on THIS device: `auto` follows the PQFORCE account (and the phone while the
 * account states none), `de`/`en` override it. Remembered on the device; `auto` is the default.
 */
export type LanguageChoice = "auto" | AppLanguage;

export const LANGUAGE_CHOICES: readonly LanguageChoice[] = ["auto", "de", "en"];

/** A stored value as a choice — anything unreadable is `auto`, which is what nobody choosing means. */
export function languageChoiceOf(value: unknown): LanguageChoice {
    return value === "de" || value === "en" ? value : "auto";
}

/**
 * The language the app speaks: the person's own choice, else the account's, else the phone's.
 *
 * ⚠ The account's language is still LEARNED while a choice overrides it — so going back to `auto`
 * lands on the account's answer at once, not on the phone's until the next start.
 */
export function effectiveLanguage(
    choice: LanguageChoice,
    account: AppLanguage | null,
    phone: AppLanguage,
): AppLanguage {
    if (choice !== "auto") return choice;
    return account ?? phone;
}

/**
 * A choice's label in the account sheet. The two languages are named in THEMSELVES — somebody who
 * cannot read the language on screen has to be able to find their own.
 */
export function languageChoiceLabel(choice: LanguageChoice): string {
    if (choice === "de") return "Deutsch";
    if (choice === "en") return "English";
    return t("mobileSheetLanguageAuto", "From PQFORCE");
}

/** The locale dates are written in: day-first in both, the month named in the reader's language. */
export function dateLocale(): string {
    return current === "de" ? "de-CH" : "en-GB";
}

/**
 * The text for `id` in the current language, else `fallback` — the code's own English.
 *
 * `{name}` placeholders are filled from `values`, EVERY occurrence, so a translator can move a value
 * within the sentence (German word order routinely does).
 */
export function t(id: string, fallback: string, values?: Record<string, string | number>): string {
    const text = dictionaries[current][id] || fallback;
    if (!values) return text;

    return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}
